import React, {useEffect, useState} from "react";
import {Alert, Button, Card, Col, Form, Row, Spinner} from "react-bootstrap";
import {fetchGlobalSettings, updateGlobalSettings, uploadDashboardPromotionImage} from "../../http/bookAPI";
import AdminDashboardPromotionProofs from "./AdminDashboardPromotionProofs";

const EMPTY_PROMOTION = {
    active: false,
    promotion_key: "",
    card_title: "",
    card_text: "",
    cta_label: "Learn more",
    detail_title: "",
    detail_text: "",
    image: "",
};

const resolveImageUrl = (image) => {
    if (!image) return "";
    if (/^(data:|https?:\/\/)/i.test(image)) return image;
    const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}${image.startsWith("/") ? image : `/${image}`}`;
};

const makePromotionKey = (value) => (
    String(value || "dashboard-promotion")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80) || "dashboard-promotion"
);

const AdminDashboardPromotion = () => {
    const [form, setForm] = useState(EMPTY_PROMOTION);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        let canceled = false;

        (async () => {
            setLoading(true);
            setError("");
            try {
                const data = await fetchGlobalSettings();
                const incoming = data?.promotions_config?.dashboard_promotion || {};
                if (!canceled) {
                    const merged = {...EMPTY_PROMOTION, ...incoming};
                    setForm({
                        ...merged,
                        promotion_key: merged.promotion_key || makePromotionKey(merged.detail_title || merged.card_title),
                    });
                }
            } catch (e) {
                if (!canceled) setError(e?.response?.data?.message || "Failed to load promotion settings.");
            } finally {
                if (!canceled) setLoading(false);
            }
        })();

        return () => {
            canceled = true;
        };
    }, []);

    const onChange = (key, value) => {
        setForm((prev) => ({...prev, [key]: value}));
        setError("");
        setSuccess("");
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Please choose an image file.");
            return;
        }

        const formData = new FormData();
        formData.append("image", file);
        setSaving(true);
        setError("");
        setSuccess("");
        uploadDashboardPromotionImage(formData)
            .then((data) => {
                onChange("image", data?.image || "");
                setSuccess("Image uploaded. Save promotion to apply it.");
            })
            .catch((e) => setError(e?.response?.data?.message || "Could not upload the selected image."))
            .finally(() => setSaving(false));
    };

    const save = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = {
                promotions_config: {
                    dashboard_promotion: {
                        ...form,
                        promotion_key: makePromotionKey(form.promotion_key || form.detail_title || form.card_title),
                        card_title: String(form.card_title || "").trim(),
                        card_text: String(form.card_text || "").trim(),
                        cta_label: String(form.cta_label || "Learn more").trim(),
                        detail_title: String(form.detail_title || "").trim(),
                        detail_text: String(form.detail_text || "").trim(),
                        image: String(form.image || "").trim(),
                    },
                },
            };
            const saved = await updateGlobalSettings(payload);
            const nextForm = {...EMPTY_PROMOTION, ...(saved?.promotions_config?.dashboard_promotion || payload.promotions_config.dashboard_promotion)};
            setForm({
                ...nextForm,
                promotion_key: nextForm.promotion_key || makePromotionKey(nextForm.detail_title || nextForm.card_title),
            });
            setSuccess("Dashboard promotion saved.");
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to save promotion settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card className="shadow-sm">
                <Card.Body className="d-flex align-items-center gap-2">
                    <Spinner size="sm" />
                    <span className="text-muted">Loading promotion settings...</span>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold text-success">Dashboard Promotion</span>
                <Form.Check
                    type="switch"
                    id="dashboard-promotion-active"
                    label="Active"
                    checked={!!form.active}
                    onChange={(e) => onChange("active", e.target.checked)}
                />
            </Card.Header>
            <Card.Body>
                <Row className="g-3">
                    <Col md={6} xs={12}>
                        <div className="fw-semibold mb-2">Dashboard block</div>
                        <Form.Group className="mb-2">
                            <Form.Label>Card title</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.card_title}
                                onChange={(e) => onChange("card_title", e.target.value)}
                                placeholder="Special promotion"
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Card text</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                size="sm"
                                value={form.card_text}
                                onChange={(e) => onChange("card_text", e.target.value)}
                                placeholder="Short teaser shown above Account & Performance."
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Button label</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.cta_label}
                                onChange={(e) => onChange("cta_label", e.target.value)}
                                placeholder="Learn more"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} xs={12}>
                        <div className="fw-semibold mb-2">Full promotion view</div>
                        <Form.Group className="mb-2">
                            <Form.Label>Promotion key</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    size="sm"
                                    value={form.promotion_key}
                                    onChange={(e) => onChange("promotion_key", makePromotionKey(e.target.value))}
                                    placeholder="summer-move-50"
                                />
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => onChange("promotion_key", makePromotionKey(form.detail_title || form.card_title || Date.now()))}
                                    style={{whiteSpace: "nowrap"}}
                                >
                                    Generate
                                </Button>
                            </div>
                            <Form.Text className="text-muted">
                                Change this when you launch a new promotion, so applications stay separated.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.detail_title}
                                onChange={(e) => onChange("detail_title", e.target.value)}
                                placeholder="Promotion title"
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Text</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                size="sm"
                                value={form.detail_text}
                                onChange={(e) => onChange("detail_text", e.target.value)}
                                placeholder="Full promotion conditions and explanation."
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Image</Form.Label>
                            <Form.Control size="sm" type="file" accept="image/*" onChange={handleImageChange} />
                        </Form.Group>
                        {form.image && (
                            <div className="border rounded p-2 bg-light">
                                <img
                                    src={resolveImageUrl(form.image)}
                                    alt="Promotion preview"
                                    className="img-fluid rounded"
                                    style={{maxHeight: 220, objectFit: "contain"}}
                                />
                                <div className="mt-2">
                                    <Button variant="outline-danger" size="sm" onClick={() => onChange("image", "")}>
                                        Remove image
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>

                {error && <Alert variant="danger" className="py-2 mt-3">{error}</Alert>}
                {success && <Alert variant="success" className="py-2 mt-3">{success}</Alert>}

                <div className="d-flex justify-content-end mt-3">
                    <Button variant="success" size="sm" onClick={save} disabled={saving}>
                        {saving ? "Saving..." : "Save Promotion"}
                    </Button>
                </div>
            </Card.Body>
        </Card>
        <AdminDashboardPromotionProofs promotion={form} />
        </>
    );
};

export default AdminDashboardPromotion;
